import { GridRowId } from '@mui/x-data-grid/models/gridRows'
import { Processing } from '@paywithextend/components'
import axios from 'axios'
import { stringify } from 'query-string'
import React from 'react'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import {
  createSetIsToastOpen,
  createSetUnhideToast,
} from 'src/app/Toast/redux/toast.actions'
import {
  AsyncBulkTransactionUpdateCompleteToastMessage,
  AsyncBulkTransactionUpdateInitiatedToastMessage,
  AsyncBulkTransactionUpdatePartialCompleteToastMessage,
  AsyncBulkTransactionUpdateToastMessage,
} from 'src/app/Toast/Toast'
import SvgAlertCircledFilled2 from 'src/components/Svgs/SvgAlertCircledFilled2'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { AsyncJobType, HidableToastNames } from 'src/enums'
import { SpendMetricsResponse } from 'src/models/spendMetrics.models'
import {
  TransactionDetailResponse,
  TransactionListResponse,
} from 'src/models/transaction.models'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { PostSpendMetricsRequestParams } from 'src/requests/postSpendMetrics.request'
import { BackendErrorPayload } from 'src/utils/error.utils'
import { css } from 'styled-components'

export const GET_TRANSACTION_INIT = 'data/transactions/GET_TRANSACTION_INIT'
export const GET_TRANSACTION_SUCCESS =
  'data/transactions/GET_TRANSACTION_SUCCESS'
export const GET_TRANSACTION_FAILURE =
  'data/transactions/GET_TRANSACTION_FAILURE'

export type GetTransactionSuccessPayload = TransactionDetailResponse

export interface GetTransactionInitAction {
  type: typeof GET_TRANSACTION_INIT
}
export interface GetTransactionSuccessAction {
  type: typeof GET_TRANSACTION_SUCCESS
  payload: GetTransactionSuccessPayload
}
export interface GetTransactionFailureAction {
  type: typeof GET_TRANSACTION_FAILURE
  payload: unknown
}

export type GetTransactionActions =
  | GetTransactionInitAction
  | GetTransactionSuccessAction
  | GetTransactionFailureAction

export const GET_REPORT_TRANSACTIONS_INIT =
  'data/transactions/GET_REPORT_TRANSACTIONS_INIT'
export const GET_REPORT_TRANSACTIONS_SUCCESS =
  'data/transactions/GET_REPORT_TRANSACTIONS_SUCCESS'
export const GET_REPORT_TRANSACTIONS_FAILURE =
  'data/transactions/GET_REPORT_TRANSACTIONS_FAILURE'

export interface GetReportTransactionsInitPayload {
  queryParams: GetReportTransactionsQueryParams
}

export interface GetReportTransactionsSuccessPayload {
  report: {
    transactions: TransactionListResponse[]
    page: number
    perPage: number
    count: number
    numPages: number
  }
  queryParams: GetReportTransactionsQueryParams
}

export interface GetReportTransactionsFailurePayload {
  queryParams: GetReportTransactionsQueryParams
}

export interface GetReportTransactionsInitAction {
  type: typeof GET_REPORT_TRANSACTIONS_INIT
  payload: GetReportTransactionsInitPayload
}
export interface GetReportTransactionsSuccessAction {
  type: typeof GET_REPORT_TRANSACTIONS_SUCCESS
  payload: GetReportTransactionsSuccessPayload
}
export interface GetReportTransactionsFailureAction {
  type: typeof GET_REPORT_TRANSACTIONS_FAILURE
  payload: GetReportTransactionsFailurePayload
}

export type GetReportTransactionsActions =
  | GetReportTransactionsInitAction
  | GetReportTransactionsSuccessAction
  | GetReportTransactionsFailureAction

const createGetReportTransactionsInitAction = (
  payload: GetReportTransactionsInitPayload,
): GetReportTransactionsInitAction => ({
  type: GET_REPORT_TRANSACTIONS_INIT,
  payload,
})
const createGetReportTransactionsSuccessAction = (
  payload: GetReportTransactionsSuccessPayload,
): GetReportTransactionsSuccessAction => ({
  type: GET_REPORT_TRANSACTIONS_SUCCESS,
  payload,
})
const createGetReportTransactionsFailureAction = (
  payload: GetReportTransactionsFailurePayload,
): GetReportTransactionsFailureAction => ({
  type: GET_REPORT_TRANSACTIONS_FAILURE,
  payload,
})

export interface GetReportTransactionsQueryParams {
  search?: string
  creditCardId?: string[]
  includeBudgets?: boolean
  virtualCardId?: string[]
  status?: string[]
  source?: string[]
  since?: string
  until?: string
  perPage?: number
  page?: number
  sort?: string
  mccGroup?: string[]
  recipientId?: string[]
  organizationExpenseReportId?: string
}

export const getReportTransactions = (
  queryParams: GetReportTransactionsQueryParams,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetReportTransactionsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetReportTransactionsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetReportTransactionsInitAction({ queryParams }))
    return axios
      .get(
        `/reports/transactions/v2?${stringify(queryParams)}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createGetReportTransactionsSuccessAction({
              ...response.data,
              queryParams,
            }),
          )
        },
        (err) => {
          dispatch(
            createGetReportTransactionsFailureAction({
              ...err.response.data,
              queryParams,
            }),
          )
        },
      )
  }
}

export const GET_YOUR_REPORT_TRANSACTIONS_INIT =
  'data/transactions/GET_YOUR_REPORT_TRANSACTIONS_INIT'
export const GET_YOUR_REPORT_TRANSACTIONS_SUCCESS =
  'data/transactions/GET_YOUR_REPORT_TRANSACTIONS_SUCCESS'
export const GET_YOUR_REPORT_TRANSACTIONS_FAILURE =
  'data/transactions/GET_YOUR_REPORT_TRANSACTIONS_FAILURE'

export interface GetReportYourTransactionsInitAction {
  type: typeof GET_YOUR_REPORT_TRANSACTIONS_INIT
  payload: GetReportTransactionsInitPayload
}
export interface GetReportYourTransactionsSuccessAction {
  type: typeof GET_YOUR_REPORT_TRANSACTIONS_SUCCESS
  payload: GetReportTransactionsSuccessPayload
}
export interface GetReportYourTransactionsFailureAction {
  type: typeof GET_YOUR_REPORT_TRANSACTIONS_FAILURE
  payload: GetReportTransactionsFailurePayload
}

export type GetReportYourTransactionsActions =
  | GetReportYourTransactionsInitAction
  | GetReportYourTransactionsSuccessAction
  | GetReportYourTransactionsFailureAction

// TODO: these actions don't belong in here, but where do they?
export const POST_SPEND_METRICS_INIT =
  'data/transactions/POST_SPEND_METRICS_INIT'
export const POST_SPEND_METRICS_SUCCESS =
  'data/transactions/POST_SPEND_METRICS_SUCCESS'
export const POST_SPEND_METRICS_FAILURE =
  'data/transactions/POST_SPEND_METRICS_FAILURE'

export type PostSpendMetricsSuccessPayload = SpendMetricsResponse

export interface PostSpendMetricsInitAction {
  type: typeof POST_SPEND_METRICS_INIT
}
export interface PostSpendMetricsSuccessAction {
  type: typeof POST_SPEND_METRICS_SUCCESS
  payload: PostSpendMetricsSuccessPayload
}
export interface PostSpendMetricsFailureAction {
  type: typeof POST_SPEND_METRICS_FAILURE
  payload: unknown
}

export type PostSpendMetricsActions =
  | PostSpendMetricsInitAction
  | PostSpendMetricsSuccessAction
  | PostSpendMetricsFailureAction

const createPostSpendMetricsInitAction = (): PostSpendMetricsInitAction => ({
  type: POST_SPEND_METRICS_INIT,
})
const createPostSpendMetricsSuccessAction = (
  payload: PostSpendMetricsSuccessPayload,
): PostSpendMetricsSuccessAction => ({
  type: POST_SPEND_METRICS_SUCCESS,
  payload,
})
const createPostSpendMetricsFailureAction = (
  err: unknown,
): PostSpendMetricsFailureAction => ({
  type: POST_SPEND_METRICS_FAILURE,
  payload: err,
})

export const postSpendMetrics = (
  body: PostSpendMetricsRequestParams,
): ThunkAction<Promise<void>, RootState, void, PostSpendMetricsActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostSpendMetricsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostSpendMetricsInitAction())
    return axios
      .post(
        `/metrics/spend`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostSpendMetricsSuccessAction(response.data))
        },
        (err) => {
          dispatch(createPostSpendMetricsFailureAction(err.response.data))
        },
      )
  }
}

export type UpdateSelectedTransactionActions = UpdateSelectedTransactionAction
export const UPDATE_SELECTED_TRANSACTIONS =
  'data/transactions/UPDATE_SELECTED_TRANSACTIONS'
export interface UpdateSelectedTransactionAction {
  type: typeof UPDATE_SELECTED_TRANSACTIONS
  payload: {
    selectedTransactions: Set<GridRowId>
  }
}
export const updateSelectedTransactions = (
  selectedTransactions?: Set<GridRowId>,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, UpdateSelectedTransactionAction>,
  ): void => {
    dispatch({
      type: UPDATE_SELECTED_TRANSACTIONS,
      payload: {
        selectedTransactions: selectedTransactions ?? new Set(),
      },
    })
  }
}

export type UpdateSelectAllTransactionsBoxVisibleActions =
  UpdateSelectAllTransactionsBoxVisibleAction
export const UPDATE_SELECT_ALL_TRANSACTIONS_BOX_VISIBLE =
  'data/transactions/UPDATE_SELECT_ALL_TRANSACTIONS_BOX_VISIBLE'
export interface UpdateSelectAllTransactionsBoxVisibleAction {
  type: typeof UPDATE_SELECT_ALL_TRANSACTIONS_BOX_VISIBLE
  payload: {
    selectAllTransactionsBoxVisible: boolean
  }
}
export const updateSelectAllTransactionsBoxVisible = (
  selectAllTransactionsBoxVisible: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      UpdateSelectAllTransactionsBoxVisibleAction
    >,
  ): void => {
    dispatch({
      type: UPDATE_SELECT_ALL_TRANSACTIONS_BOX_VISIBLE,
      payload: {
        selectAllTransactionsBoxVisible,
      },
    })
  }
}

export type UpdateSelectAllTransactionsFiltersActions =
  UpdateSelectAllTransactionsFiltersAction
export const UPDATE_SELECT_ALL_TRANSACTIONS_FILTERS =
  'data/transactions/UPDATE_SELECT_ALL_TRANSACTIONS_FILTERS'
export interface UpdateSelectAllTransactionsFiltersAction {
  type: typeof UPDATE_SELECT_ALL_TRANSACTIONS_FILTERS
  payload: {
    selectAllTransactionsFilters: TransactionsFiltersBody | undefined
  }
}
export const updateSelectAllTransactionsFilters = (
  selectAllTransactionsFilters: TransactionsFiltersBody | undefined,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      UpdateSelectAllTransactionsFiltersAction
    >,
  ): void => {
    dispatch({
      type: UPDATE_SELECT_ALL_TRANSACTIONS_FILTERS,
      payload: {
        selectAllTransactionsFilters,
      },
    })
  }
}

export const GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_INIT =
  'data/virtualCards/GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_INIT'
export const GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_SUCCESS =
  'data/virtualCards/GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_SUCCESS'
export const GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_FAILURE =
  'data/virtualCards/GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_FAILURE'
export const GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_COMPLETE =
  'data/virtualCards/GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_COMPLETE'

export type GetAsyncTransactionUpdateJobStatusSuccessPayload = {
  jobId: string
  jobType: AsyncJobType
  total: number
  successful: number
  remaining: number
}

export type GetAsyncTransactionUpdateJobStatusFailurePayload =
  BackendErrorPayload<string>

export interface GetAsyncTransactionUpdateJobStatusInitAction {
  type: typeof GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_INIT
}
export interface GetAsyncTransactionUpdateJobStatusSuccessAction {
  type: typeof GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_SUCCESS
  payload: GetAsyncTransactionUpdateJobStatusSuccessPayload
}
export interface GetAsyncTransactionUpdateJobStatusFailureAction {
  type: typeof GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_FAILURE
  payload: GetAsyncTransactionUpdateJobStatusFailurePayload
}
export interface GetAsyncTransactionUpdateJobStatusCompleteAction {
  type: typeof GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_COMPLETE
  payload: GetAsyncTransactionUpdateJobStatusSuccessPayload
}

export type GetAsyncTransactionUpdateJobStatusActions =
  | GetAsyncTransactionUpdateJobStatusInitAction
  | GetAsyncTransactionUpdateJobStatusSuccessAction
  | GetAsyncTransactionUpdateJobStatusFailureAction
  | GetAsyncTransactionUpdateJobStatusCompleteAction

const createGetAsyncTransactionUpdateJobStatusInitAction =
  (): GetAsyncTransactionUpdateJobStatusInitAction => ({
    type: GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_INIT,
  })
const createGetAsyncTransactionUpdateJobStatusSuccessAction = (
  payload: GetAsyncTransactionUpdateJobStatusSuccessPayload,
): GetAsyncTransactionUpdateJobStatusSuccessAction => ({
  type: GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_SUCCESS,
  payload,
})
const createGetAsyncTransactionUpdateJobStatusFailureAction = (
  response: GetAsyncTransactionUpdateJobStatusFailurePayload,
): GetAsyncTransactionUpdateJobStatusFailureAction => ({
  type: GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_FAILURE,
  payload: response,
})
const createGetAsyncTransactionUpdateJobStatusCompleteAction = (
  payload: GetAsyncTransactionUpdateJobStatusSuccessPayload,
): GetAsyncTransactionUpdateJobStatusCompleteAction => ({
  type: GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_COMPLETE,
  payload,
})

export const getAsyncTransactionUpdateJobStatus = (
  id: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetAsyncTransactionUpdateJobStatusActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetAsyncTransactionUpdateJobStatusActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetAsyncTransactionUpdateJobStatusInitAction())
    const { bulk_select_fast_follows } =
      selectFeatureFlaggerFromState(getState())
    if (bulk_select_fast_follows) {
      // initial toast message
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <AsyncBulkTransactionUpdateInitiatedToastMessage />,
          hidableToastName: HidableToastNames.BulkTransactionUpdate,
          inProgressColorScheme: true,
          image: (
            <Processing
              css={css`
                transform: scale(1.2);
              `}
            />
          ),
          bulkToast: true,
          isInfinite: true,
        }),
      )
    }
    return axios
      .get<GetAsyncTransactionUpdateJobStatusSuccessPayload>(
        `/asyncJobs/${id}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          const { total, successful, remaining } = response.data

          dispatch(
            createGetAsyncTransactionUpdateJobStatusSuccessAction(
              response.data,
            ),
          )

          if (remaining !== 0) {
            // in progress
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <AsyncBulkTransactionUpdateToastMessage
                    partial={total - remaining}
                    total={total}
                  />
                ),
                hidableToastName: HidableToastNames.BulkTransactionUpdate,
                inProgressColorScheme: true,
                image: (
                  <Processing
                    css={css`
                      transform: scale(1.2);
                    `}
                  />
                ),
                bulkToast: true,
                isInfinite: true,
              }),
            )
          } else {
            // success or failure
            dispatch(
              createSetUnhideToast(HidableToastNames.BulkTransactionUpdate),
            )
            if (successful !== total) {
              // failed
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: (
                    <AsyncBulkTransactionUpdatePartialCompleteToastMessage
                      partial={successful}
                      total={total}
                    />
                  ),
                  partialSuccessColorScheme: true,
                  image: (
                    <SvgAlertCircledFilled2
                      css={css`
                        transform: scale(0.8);
                        margin-left: -10px;
                      `}
                    />
                  ),
                }),
              )
            } else {
              // success
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: (
                    <AsyncBulkTransactionUpdateCompleteToastMessage />
                  ),
                }),
              )
            }
            // invalidate transactions and metrics
            dispatch(
              createGetAsyncTransactionUpdateJobStatusCompleteAction(
                response.data,
              ),
            )
          }
        },
        (err) => {
          dispatch(
            createGetAsyncTransactionUpdateJobStatusFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}
